import React from 'react';
import { motion } from 'framer-motion';
import { 
  FaLayerGroup, 
  FaBolt, 
  FaChartLine, 
  FaNetworkWired 
} from 'react-icons/fa';
import { HiSparkles } from 'react-icons/hi';
import { IconType } from 'react-icons';
import './Advantages.scss';

interface Advantage {
  id: string;
  title: string;
  description: string;
  icon: IconType;
  benefits: string[];
}

const advantages: Advantage[] = [
  {
    id: 'structure',
    title: 'Структуризация',
    description: 'Распределение задач по ответственным зонам и закрепление за сотрудниками. Легкий и интуитивный интерфейс.',
    // @ts-ignore
    icon: FaLayerGroup,
    benefits: [
      'Чёткая иерархия задач',
      'Автоматическое назначение',
      'Удобная кросс-навигация'
    ]
  },
  {
    id: 'automation',
    title: 'Автоматизация',
    description: 'Оптимизация рутинных процессов и радикальное сокращение времени на их выполнение.',
    // @ts-ignore
    icon: FaBolt,
    benefits: [
      'Триггерные уведомления',
      'Шаблоны согласований',
      'Интеграция по API'
    ]
  },
  {
    id: 'analytics',
    title: 'Точный учёт',
    description: 'Непрерывный сбор и анализ данных для принятия обоснованных управленческих решений.',
    // @ts-ignore
    icon: FaChartLine,
    benefits: [
      'Детальная статистика',
      'Оценка эффективности',
      'Экспорт отчетов'
    ]
  },
  {
    id: 'centralization',
    title: 'Единая экосистема',
    description: 'Централизация всех каналов общения в одном безопасном корпоративном пространстве.',
    // @ts-ignore
    icon: FaNetworkWired,
    benefits: [
      'Единая точка входа',
      'Вечный архив истории',
      'Синхронизация данных'
    ]
  }
];

const Advantages: React.FC = () => {
  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const renderIcon = (IconComponent: IconType, props: any = {}) => {
    // @ts-ignore
    return <IconComponent {...props} />;
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.15 } }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.16, 1, 0.3, 1] } }
  };

  return (
    <section id="advantages" className="advantages section">
      <div className="container">
        <motion.div 
          className="advantages__header"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <span className="section-badge">Преимущества</span>
          <h2 className="advantages__title">
            Почему выбирают <span className="text-gradient">beheard</span>?
          </h2>
          <p className="advantages__subtitle">
            Абсолютный контроль над рабочими процессами, который освобождает время для масштабирования вашего бизнеса.
          </p>
        </motion.div>

        <motion.div 
          className="advantages__grid"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
        >
          {advantages.map((advantage, index) => (
            <motion.div 
              key={advantage.id}
              className="adv-card"
              variants={itemVariants}
            >
              <div className="adv-card__number">
                {String(index + 1).padStart(2, '0')}
              </div>
              <div className="adv-card__content">
                <div className="adv-card__icon">
                  {renderIcon(advantage.icon)}
                </div>
                <h3 className="adv-card__title">{advantage.title}</h3>
                <p className="adv-card__description">{advantage.description}</p>
                
                <div className="adv-card__benefits">
                  {advantage.benefits.map((benefit, idx) => (
                    <div key={idx} className="benefit-item">
                      <div className="benefit-item__dot"></div>
                      <span className="benefit-item__text">{benefit}</span>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>

        <motion.div 
          className="advantages__cta"
          initial={{ opacity: 0, scale: 0.95, y: 30 }}
          whileInView={{ opacity: 1, scale: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
        >
          <div className="adv-banner">
            <h3>Готовы к цифровой трансформации?</h3>
            <p>Присоединяйтесь к компаниям, которые уже оптимизировали свои внутренние процессы с помощью нашей платформы.</p>
            <button 
              className="btn"
              onClick={() => scrollToSection('contact')}
            >
              Забронировать презентацию
            </button>
            <div className="trial-info">
              <span className="trial-info__text">Срок пробного периода — 2 месяца</span>
              <span className="trial-info__bonus">
                Настройка и онбординг команды в подарок
                {/* @ts-ignore */}
                <HiSparkles className="trial-info__bonus-icon" />
              </span>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Advantages;
